import Button from '@components/common/button/Button';
import DynamicFormFields from '@components/common/dynamicFormFields/DynamicFormFields';
import Modal from '@components/common/modal/Modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import React, { useRef } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { requiredStringValidation } from '@/constant';
import { FormField, FormFieldType } from '@/type';
import { buildValidationSchema } from '@/util';

export type CopyForm = {
  name: string;
  description?: string;
};

export interface CopyModalProps {
  open: boolean;
  initialName: string;
  initialDescription?: string;
  withDescription?: boolean;
  onClose: (open: boolean) => void;
  onSubmit: (copyForm: CopyForm, openEditor: boolean) => void | Promise<void>;
  isSavePending?: boolean;
}

const nameFormField: FormField = {
  type: FormFieldType.Text,
  label: 'common.copy-modal.name',
  name: 'name',
  validation: requiredStringValidation(),
  xs: 12,
};

const descriptionFormField: FormField = {
  type: FormFieldType.Text,
  label: 'common.description',
  name: 'description',
  isMultiRow: true,
  xs: 12,
};

const CopyModal = ({
  open,
  initialName,
  initialDescription,
  withDescription = false,
  onClose,
  onSubmit,
  isSavePending,
}: CopyModalProps) => {
  const { t } = useTranslation();
  const openEditorAfterCopyRef = useRef(false);

  const formFields = withDescription
    ? [nameFormField, descriptionFormField]
    : [nameFormField];

  const methods = useForm<CopyForm>({
    defaultValues: { name: initialName, description: initialDescription },
    resolver: yupResolver(
      buildValidationSchema(formFields) as Yup.ObjectSchema<CopyForm>,
    ) as Resolver<CopyForm>,
  });

  const isSaveDisabled = isSavePending || methods.formState.isSubmitting;

  const handleFormSubmit = (copyForm: CopyForm) => {
    const openEditor = openEditorAfterCopyRef.current;
    openEditorAfterCopyRef.current = false;
    return onSubmit(copyForm, openEditor);
  };

  return (
    <Modal title='common.form.copy' open={open} onClose={onClose} maxWidth='600px'>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
          <Grid container spacing={2}>
            <DynamicFormFields<CopyForm>
              formFields={formFields}
              control={methods.control}
            />
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant='outlined'
              onClick={() => onClose(false)}
              disabled={isSaveDisabled}
            >
              {t('common.form.cancel')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              disabled={isSaveDisabled}
            >
              {t('common.form.copy')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              disabled={isSaveDisabled}
              onClick={() => {
                openEditorAfterCopyRef.current = true;
              }}
            >
              {t('common.copy-modal.copy-and-edit')}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default CopyModal;
